// Locale detection for the extension.
// The app strictly follows the browser/OS UI language (auto-detect only — no
// user-facing picker). Falls back to English when the detected locale isn't
// one we support.

import { ext } from '../utils/browserApi';

/** Languages we ship. `en` is the fallback. Keep in sync with src/i18n/locales/. */
export const SUPPORTED_LOCALES = [
  'en', // English (fallback)
  'ar', // Arabic
  'ur', // Urdu
  'fa', // Persian / Farsi
  'id', // Indonesian
  'tr', // Turkish
  'fr', // French
  'bn', // Bengali
  'ms', // Malay
  'ha', // Hausa
  'sw', // Swahili
  'it', // Italian
  'es', // Spanish
  'sq', // Albanian
  'ru', // Russian
] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

/** Right-to-left languages. */
export const RTL_LOCALES = new Set<string>(['ar', 'ur', 'fa']);

export const DEFAULT_LOCALE: SupportedLocale = 'en';

const isSupported = (lng: string): lng is SupportedLocale =>
  (SUPPORTED_LOCALES as readonly string[]).includes(lng);

/** Normalize a raw tag like "fr-FR" / "pt_BR" to its base "fr". */
const baseTag = (raw: string): string => raw.toLowerCase().split(/[-_]/)[0];

/**
 * Dev-only locale override so all 11 locales can be tested without changing the
 * actual browser UI language (chrome.i18n.getUILanguage can't be set per-tab).
 * Use `?lng=ar` in the URL or `localStorage.dev_locale = 'ar'`.
 */
const devOverride = (): string | null => {
  if (process.env.NODE_ENV !== 'development') return null;
  if (typeof window === 'undefined') return null;
  try {
    const fromQuery = new URLSearchParams(window.location.search).get('lng');
    if (fromQuery) return fromQuery;
    return window.localStorage.getItem('dev_locale');
  } catch {
    return null;
  }
};

/**
 * Detect the active locale.
 * Extension pages: chrome.i18n.getUILanguage(). Plain web contexts (the hosted
 * account/billing/privacy pages) have no `ext.i18n`, so fall back to
 * navigator.language. Unsupported locales collapse to English.
 */
export const detectLocale = (): SupportedLocale => {
  const override = devOverride();
  if (override && isSupported(baseTag(override))) return baseTag(override) as SupportedLocale;

  const fromExt =
    typeof ext?.i18n?.getUILanguage === 'function' ? ext.i18n.getUILanguage() : '';
  const fromNav =
    typeof navigator !== 'undefined' && navigator.language ? navigator.language : '';
  const base = baseTag(fromExt || fromNav || DEFAULT_LOCALE);

  return isSupported(base) ? base : DEFAULT_LOCALE;
};

export const isRtl = (lng: string): boolean => RTL_LOCALES.has(baseTag(lng));
